import {
  AlignJustify, Facebook, Linkedin, X,
  Building2, Briefcase, Code, Cloud, ShoppingCart, Shield,
  Palette, Server, ChevronDown, ChevronUp, Brain, Globe,
  Package, Megaphone, Phone,
} from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Companylogo from "../../../../logo.png";
import { Link } from "react-router-dom";

interface PopupItem {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  link?: string;
}

const industriesData: PopupItem[] = [
  { title: "Healthcare", description: "Digital health solutions and medical technology", icon: Building2 },
  { title: "Finance & Banking", description: "Fintech solutions and secure banking systems", icon: Briefcase },
  { title: "Retail & E-commerce", description: "Online shopping platforms and retail tech", icon: ShoppingCart },
  { title: "Engineering & Construction", description: "Engineering and construction solutions", icon: Building2 },
  { title: "Education", description: "EdTech platforms and learning management systems", icon: Building2 },
  { title: "Real Estate", description: "Property management and real estate tech", icon: Building2 },
  { title: "Transportation", description: "Logistics and transportation management", icon: Building2 },
  { title: "Energy & Utilities", description: "Smart grid and energy management solutions", icon: Building2 },
];

const servicesData: PopupItem[] = [
  { title: "AI Solutions", description: "Machine learning and AI-powered applications", icon: Brain, link: "/ai-solutions" },
  { title: "Cloud Solutions", description: "Scalable cloud infrastructure and migration", icon: Cloud, link: "/cloud-solutions" },
  { title: "Application Development", description: "Bespoke software tailored to your needs", icon: Code, link: "/application-development" },
  { title: "E-commerce Solutions", description: "Online stores and marketplace platforms", icon: ShoppingCart, link: "/e-commerce-solutions" },
  { title: "IT Management", description: "Network management and IT support", icon: Package, link: "/it-service-package" },
  { title: "UI/UX Design", description: "User-centered design and experience", icon: Palette, link: "/ui-ux-design" },
  { title: "Web Hosting & Domain", description: "Reliable hosting and domain services", icon: Globe, link: "/domains" },
  { title: "Cybersecurity Services", description: "Security audits and protection", icon: Shield, link: "/cybersecurity-services" },
  { title: "IT consulting", description: "Expert IT consulting and advisory services", icon: Briefcase, link: "/it-consulting" },
  { title: "NAS", description: "Network Attached Storage solutions", icon: Server, link: "/nas" },
  { title: "Marketing", description: "Digital marketing and advertising solutions", icon: Megaphone, link: "/marketing" },
  { title: "VoIP Service", description: "Voice over IP and communication solutions", icon: Phone, link: "/voip-service" },
];

function AccordionPanel({
  isOpen,
  children,
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    } else {
      setHeight(0);
      const t = setTimeout(() => setVisible(false), 350);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!visible || !innerRef.current) return;
    const el = innerRef.current;
    const ro = new ResizeObserver(() => {
      if (isOpen) setHeight(el.scrollHeight);
    });
    ro.observe(el);
    setHeight(el.scrollHeight);
    return () => ro.disconnect();
  }, [visible, isOpen]);

  if (!visible && !isOpen) return null;

  return (
    <div
      style={{
        height,
        overflow: "hidden",
        transition: "height 350ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        ref={innerRef}
        style={{
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "translateY(0)" : "translateY(-8px)",
          transition: "opacity 300ms ease, transform 300ms ease",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function Offcanvas() {
  const [open, setOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ✅ Removed body scroll lock entirely — useFullPageScroll manages overflow.
  // Wheel/touch events are blocked via onWheel/onTouchMove stopPropagation on the portal elements.

  const closeMenu = useCallback(() => {
    window.dispatchEvent(new CustomEvent("offcanvas:close"));
    setOpen(false);
    setHoveredItem(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent("offcanvas:open"));
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!panelRef.current?.contains(t) && !popupRef.current?.contains(t)) closeMenu();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, closeMenu]);

  const clearHover = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
  };

  useEffect(() => {
    if (isMobile || !hoveredItem) return;

    const onMove = (e: MouseEvent) => {
      const overPanel = panelRef.current?.contains(e.target as Node) ?? false;
      const overPopup = popupRef.current?.contains(e.target as Node) ?? false;
      if (overPanel || overPopup) {
        clearHover();
      } else {
        if (!hoverTimer.current) {
          hoverTimer.current = setTimeout(() => {
            setHoveredItem(null);
            hoverTimer.current = null;
          }, 350);
        }
      }
    };

    window.addEventListener("mousemove", onMove);
    return () => { window.removeEventListener("mousemove", onMove); clearHover(); };
  }, [isMobile, hoveredItem]);

  const onEnter = (item: string) => { if (isMobile) return; clearHover(); setHoveredItem(item); };
  const onLeave = () => { };

  const handleToggle = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    if (open) { closeMenu(); setExpandedMobile(null); }
    else { setOpen(true); setExpandedMobile(null); }
    setTimeout(() => setIsAnimating(false), 400);
  };

  const handleServiceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearHover();
    closeMenu();
  };

  const toggleMobile = (key: string) =>
    setExpandedMobile(prev => (prev === key ? null : key));

  const stopScroll = (e: React.WheelEvent | React.TouchEvent) => e.stopPropagation();

  const portalContent = (
    <>
      {isAnimating && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 2147483640 }}>
          <div style={{
            position: "absolute", top: 20, right: 20, width: 48, height: 48, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(6,182,212,0.4), rgba(139,92,246,0.2))",
            animation: "ocRipple 0.6s ease-out forwards",
          }} />
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/60"
          style={{ zIndex: 2147483641, animation: "ocFadeIn 0.25s ease-out" }}
          onClick={closeMenu}
          onWheel={stopScroll}
          onTouchMove={stopScroll}
        />
      )}

      {!isMobile && (hoveredItem === "industries" || hoveredItem === "services") && (
        <div
          ref={popupRef}
          className="fixed top-0 left-0 hidden lg:block pointer-events-auto overflow-hidden"
          style={{
            width: open ? "calc(100% - 350px)" : "100%",
            height: "100vh",
            zIndex: 2147483642,
            animation: "ocSlideIn 0.3s ease-out both",
            background: "#1a1a1a",
          }}
          onWheel={stopScroll}
          onTouchMove={stopScroll}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.45) 100%)" }}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-10">
            <h2
              className="text-4xl font-bold text-center mb-10 flex-shrink-0"
              style={{
                background: "linear-gradient(90deg, #e2e8f0 0%, #94a3b8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {hoveredItem === "industries" ? "Our Industries" : "Our Services"}
            </h2>
            <div
              className={`grid w-full max-w-6xl ${hoveredItem === "services"
                ? "grid-cols-3 lg:grid-cols-3 xl:grid-cols-4"
                : "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                }`}
              style={{ gap: "18px" }}
            >
              {(hoveredItem === "industries" ? industriesData : servicesData).map((item, i) => {
                const Icon = item.icon;
                const inner = (
                  <div className="flex flex-col items-center text-center">
                    <div className="flex-shrink-0 p-3.5 rounded-xl bg-gradient-to-br from-purple-600/30 via-blue-500/25 to-cyan-400/30 border border-cyan-400/40 mb-3">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <span className="font-semibold text-gray-200 group-hover:text-white transition-colors leading-tight">
                      {item.title}
                    </span>
                    {hoveredItem === "industries" && (
                      <p className="text-sm text-gray-300 group-hover:text-gray-300 mt-2 leading-snug transition-colors">
                        {item.description}
                      </p>
                    )}
                  </div>
                );
                const cls = `group relative rounded-2xl px-4 py-6 border transition-all duration-300 hover:scale-105 hover:shadow-xl`;
                const baseStyle = {
                  background: "#1a1a1a",
                  borderColor: "rgba(255,255,255,0.08)",
                  animation: `ocCardUp 0.3s ease-out ${i * 0.03}s both`,
                };
                if (hoveredItem === "industries") {
                  return <div key={i} className={`${cls} cursor-default`} style={baseStyle}>{inner}</div>;
                }
                return item.link ? (
                  <Link
                    key={i} to={item.link} className={cls} style={baseStyle}
                    onClick={handleServiceClick} onMouseDown={e => e.stopPropagation()}
                  >
                    {inner}
                  </Link>
                ) : (
                  <div key={i} className={cls} style={baseStyle}>{inner}</div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Side panel */}
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 h-[100dvh] w-full sm:w-[350px] flex flex-col
          transform transition-transform duration-500 ease-out
          ${open ? "translate-x-0" : "translate-x-full"}`}
        style={{
          zIndex: 2147483643,
          background: "rgba(0,0,0,0.88)",
          borderLeft: open ? "1px solid rgba(255,255,255,0.08)" : "none",
          boxShadow: open ? "-12px 0 60px rgba(0,0,0,0.95)" : "none",
        }}
        onWheel={stopScroll}
        onTouchMove={stopScroll}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div style={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)", top: "-15%", right: "-10%" }} />
          <div style={{ position: "absolute", width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)", bottom: "-10%", left: "-8%" }} />
        </div>

        <div
          className="relative z-10 flex-shrink-0 flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
        >
          <Link to="/" onClick={closeMenu} className="flex items-center gap-3 group">
            <img
              src={Companylogo}
              alt="SAN micron"
              className="h-14 w-auto drop-shadow-lg transition-transform group-hover:scale-110 group-hover:rotate-3"
            />
            <span className="text-lg font-bold text-white">SAN micron</span>
          </Link>
          <button
            onClick={closeMenu}
            className="p-1.5 rounded-lg text-white hover:text-cyan-400 hover:bg-white/10 transition-all"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <div
          className="relative z-10 flex-1 px-6 sm:px-8 py-6"
          style={{
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          } as React.CSSProperties}
        >
          <p className="text-gray-200 text-lg leading-relaxed mb-8">
            Trusted IT partner driving innovation and business success through technology.
          </p>
          <ul className="flex flex-col gap-5">
            <li>
              <button
                className="w-full text-left text-base font-bold text-white hover:text-cyan-400 transition-colors flex items-center justify-between"
                onClick={() => isMobile && toggleMobile("industries")}
                onMouseEnter={() => !isMobile && onEnter("industries")}
                onMouseLeave={() => !isMobile && onLeave()}
              >
                <span>Industries</span>
                {isMobile && (expandedMobile === "industries" ? <ChevronUp size={17} /> : <ChevronDown size={17} />)}
              </button>
              {isMobile && (
                <AccordionPanel isOpen={expandedMobile === "industries"}>
                  <div className="mt-3 pl-3 space-y-2">
                    {industriesData.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5">
                          <div className="flex-shrink-0 p-3.5 rounded-xl bg-gradient-to-br from-purple-600/30 via-blue-500/25 to-cyan-400/30 border border-cyan-400/40">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium text-white">{item.title}</span>
                        </div>
                      );
                    })}
                  </div>
                </AccordionPanel>
              )}
            </li>

            <li>
              <button
                className="w-full text-left text-base font-bold text-white hover:text-cyan-400 transition-colors flex items-center justify-between"
                onClick={() => isMobile && toggleMobile("services")}
                onMouseEnter={() => !isMobile && onEnter("services")}
                onMouseLeave={() => !isMobile && onLeave()}
              >
                <span>Services</span>
                {isMobile && (expandedMobile === "services" ? <ChevronUp size={17} /> : <ChevronDown size={17} />)}
              </button>
              {isMobile && (
                <AccordionPanel isOpen={expandedMobile === "services"}>
                  <div className="mt-3 pl-3 space-y-2 pb-2">
                    {servicesData.map((item, i) => {
                      const Icon = item.icon;
                      const inner = (
                        <div className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-all">
                          <div className="flex-shrink-0 p-3.5 rounded-xl bg-gradient-to-br from-purple-600/30 via-blue-500/25 to-cyan-400/30 border border-cyan-400/40">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium text-white">{item.title}</span>
                        </div>
                      );
                      return item.link
                        ? <Link key={i} to={item.link} onClick={closeMenu} className="block">{inner}</Link>
                        : <div key={i}>{inner}</div>;
                    })}
                  </div>
                </AccordionPanel>
              )}
            </li>

            {/* ── Mobile-only: Websites & Emails ── */}
            {isMobile && (
              <>
                <li>
                  <Link
                    to="/websites"
                    onClick={closeMenu}
                    className="text-base font-bold text-white hover:text-cyan-400 transition-colors"
                  >
                    Websites
                  </Link>
                </li>
                <li>
                  <Link
                    to="/email-hosting"
                    onClick={closeMenu}
                    className="text-base font-bold text-white hover:text-cyan-400 transition-colors"
                  >
                    Emails
                  </Link>
                </li>
              </>
            )}

            {[
              { to: "/about", label: "About us" },
              { to: "/newsroom", label: "Blog" },
              { to: "/careers", label: "Careers" },
              { to: "/support", label: "Support" },
              { to: "/contact-us", label: "Contact us" },
              { to: "/refer-and-earn", label: "Refer and Earn" },
              { to: "/social", label: "Social" },
            ].map(({ to, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={closeMenu}
                  onMouseEnter={() => { if (!isMobile) { clearHover(); setHoveredItem(null); } }}
                  className="text-base font-bold text-white hover:text-cyan-400 transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}

          </ul>

          <div className="flex gap-5 mt-12 pb-6 text-white">
            <a href="https://www.facebook.com/p/SAN-micron-61569088162671/" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
              <Facebook size={24} />
            </a>
            <a href="https://www.linkedin.com/company/sanmicron/life" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
              <Linkedin size={24} />
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ocRipple  { 0%   { transform: scale(0);        opacity: .5 } 100% { transform: scale(80);       opacity: 0 } }
        @keyframes ocFadeIn  { from { opacity: 0 }                               to   { opacity: 1 } }
        @keyframes ocSlideIn { from { opacity: 0; transform: translateY(10px) }  to   { opacity: 1; transform: translateY(0) } }
        @keyframes ocCardUp  { from { opacity: 0; transform: translateY(10px) }  to   { opacity: 1; transform: translateY(0) } }
      `}</style>
    </>
  );

  return (
    <>
      <button onClick={handleToggle} className="relative group flex-shrink-0" style={{ zIndex: 9 }} aria-label="Toggle menu">
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-400/20 to-blue-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
        <div className="relative w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl
          bg-gradient-to-br from-white/15 via-white/10 to-white/5 backdrop-blur-md
          border border-white/30 group-hover:border-cyan-400/60 transition-all duration-300
          shadow-[0_8px_32px_0_rgba(0,0,0,0.3),inset_0_1px_0_0_rgba(255,255,255,0.2)]
          group-hover:shadow-[0_8px_32px_0_rgba(6,182,212,0.4)] group-hover:scale-105">
          <AlignJustify
            className="text-white group-hover:text-cyan-400 group-hover:scale-110 transition-all duration-300 drop-shadow-[0_0_8px_rgba(6,182,212,0.5)]"
            size={22} strokeWidth={2.5}
          />
        </div>
      </button>

      {createPortal(portalContent, document.body)}
    </>
  );
}